import { useTranslation } from 'react-i18next';

import { Button, Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

import PrescriptionRuleCell from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRuleCell';
import { ROW_HEIGHT } from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRulesGrid.utils';
import { dataGridSx } from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRulesGrid.styles';
import { RosterDataGrid } from 'components/shared/RosterViewDataGrid';
import {
  buildProtocolRuleRows,
  createRelativeWeekColumns,
  RELATIVE_WEEKS_VIEW
} from './ProtocolRuleGrid.utils';
import {
  ActivityCell,
  AddRuleRow,
  EmptyState,
  NoteIcon,
  RuleActionButton,
  RuleActions,
  TotalCell
} from './ProtocolRuleGrid.styles';

import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { PrescriptionRuleCellValue } from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRuleCell';
import type {
  ProtocolRuleGridProps,
  ProtocolRuleRow
} from './ProtocolRuleGrid.types';

const WEEK_COLUMN_WIDTH = 72;

const renderWeekCell = ({ value }: GridRenderCellParams<ProtocolRuleRow>) => (
  <PrescriptionRuleCell value={value as PrescriptionRuleCellValue | null} />
);

/**
 * The rules of a protocol as rows, and the weeks `w1` to the protocol's duration as columns, on the
 * same roster grid (and with the same cells) as a care receiver's prescriptions. The cells are
 * read-only: a rule is changed from the pencil on its row, and added from the plus in the header of
 * the actions column (or, while there are no rules and so no grid, from the button below the
 * empty-state text).
 */
const ProtocolRuleGrid = ({
  rules,
  weekCount,
  onAdd,
  onEdit,
  onDelete
}: ProtocolRuleGridProps) => {
  'use memo';
  const { t } = useTranslation('common');

  const addButton = (
    <AddRuleRow>
      <Button
        variant="text"
        startIcon={<Icon type="plus" fontSize="small" />}
        onClick={onAdd}
      >
        {t('adminPortal.protocols.detail.addRule')}
      </Button>
    </AddRuleRow>
  );

  if (rules.length === 0) {
    return (
      <>
        <EmptyState>
          <Typography variant="body2">
            {t('adminPortal.protocols.detail.noRules')}
          </Typography>
        </EmptyState>
        {addButton}
      </>
    );
  }

  const rows = buildProtocolRuleRows(rules, weekCount);
  const customViewBuilders = {
    [RELATIVE_WEEKS_VIEW]: createRelativeWeekColumns(
      weekCount,
      WEEK_COLUMN_WIDTH
    )
  };

  const fixedColumnsLeft: GridColDef<ProtocolRuleRow>[] = [
    {
      field: 'activity',
      headerName: t('adminPortal.protocols.detail.rule'),
      width: 250,
      renderCell: ({ row }) => (
        <ActivityCell>
          <Typography variant="body1" noWrap>
            {row.activityName}
          </Typography>
          {row.description && (
            <Tooltip title={row.description} arrow>
              <NoteIcon type="note" color="action" />
            </Tooltip>
          )}
        </ActivityCell>
      )
    }
  ];
  const fixedColumnsRight: GridColDef<ProtocolRuleRow>[] = [
    {
      field: 'total',
      headerName: t('adminPortal.protocols.detail.total'),
      headerAlign: 'center',
      width: 88,
      renderCell: ({ row }) => <TotalCell>{row.total}</TotalCell>
    },
    {
      field: 'actions',
      headerName: '',
      headerAlign: 'center',
      width: 88,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <RuleActionButton
          type="plus"
          size="small"
          aria-label={t('adminPortal.protocols.detail.addRule')}
          onClick={onAdd}
        />
      ),
      renderCell: ({ row }) => (
        <RuleActions>
          <RuleActionButton
            type="pen"
            size="small"
            aria-label={t('adminPortal.protocols.detail.editRule', {
              name: row.activityName
            })}
            onClick={() => onEdit(row.rule)}
          />
          <RuleActionButton
            type="trash-can"
            size="small"
            aria-label={t('adminPortal.protocols.detail.deleteRule', {
              name: row.activityName
            })}
            onClick={() => onDelete(row.rule)}
          />
        </RuleActions>
      )
    }
  ];

  return (
    <RosterDataGrid<ProtocolRuleRow, typeof RELATIVE_WEEKS_VIEW>
      rosterDays={[]}
      rows={rows}
      fixedColumnsLeft={fixedColumnsLeft}
      fixedColumnsRight={fixedColumnsRight}
      timelineView={RELATIVE_WEEKS_VIEW}
      customViewBuilders={customViewBuilders}
      pinnedColumns={{ left: ['activity'], right: ['total', 'actions'] }}
      renderTimelineCell={renderWeekCell}
      rowHeight={ROW_HEIGHT}
      sx={dataGridSx}
    />
  );
};

export default ProtocolRuleGrid;
